/* ═══════════════════════════════════════════════
   Nouvilo — theme.js
   Vanilla JS only. No dependencies.
═══════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Scroll reveal ──────────────────────────── */
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.reveal-up').forEach((el) => revealObserver.observe(el));

  /* ── Sticky header shrink ───────────────────── */
  const header = document.getElementById('SiteHeader');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 80);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── Mobile nav toggle ──────────────────────── */
  const navToggle = document.getElementById('nav-toggle');
  const navClose  = document.getElementById('nav-close');
  const mobileNav = document.getElementById('mobile-nav');

  function openNav() {
    mobileNav.setAttribute('aria-hidden', 'false');
    navToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    navClose && navClose.focus();
  }
  function closeNav() {
    mobileNav.setAttribute('aria-hidden', 'true');
    navToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    navToggle && navToggle.focus();
  }

  navToggle && navToggle.addEventListener('click', openNav);
  navClose  && navClose.addEventListener('click', closeNav);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileNav && mobileNav.getAttribute('aria-hidden') === 'false') closeNav();
  });

  /* ── Cart drawer ────────────────────────────── */
  const cartToggle  = document.getElementById('cart-toggle');
  const cartDrawer  = document.getElementById('cart-drawer');
  const cartOverlay = document.getElementById('cart-overlay');
  const cartClose   = cartDrawer && cartDrawer.querySelector('.cart-drawer__close');

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.show(); // non-modal: page stays interactive behind overlay
    cartOverlay && cartOverlay.classList.add('is-open');
    cartToggle && cartToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    cartClose && cartClose.focus();
  }
  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.close();
    cartOverlay && cartOverlay.classList.remove('is-open');
    cartToggle && cartToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    cartToggle && cartToggle.focus();
  }

  cartToggle  && cartToggle.addEventListener('click', openCart);
  cartClose   && cartClose.addEventListener('click', closeCart);
  cartOverlay && cartOverlay.addEventListener('click', closeCart);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer && cartDrawer.hasAttribute('open')) closeCart();
  });

  /* ── Cart drawer: remove one via minus button ── */
  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }
  cartDrawer && cartDrawer.addEventListener('click', async (e) => {
    const btn = e.target.closest('.cart-item__minus');
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    const li = btn.closest('.cart-item');
    const qtyEl = li.querySelector('.cart-item__qty');
    const newQty = Math.max(0, parseInt(qtyEl.textContent, 10) - 1);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: btn.dataset.key, quantity: newQty })
      });
      if (!res.ok) throw new Error('cart change failed');
      const cart = await res.json();
      const updated = cart.items.find((i) => i.key === btn.dataset.key);
      if (!updated) {
        li.remove();
      } else {
        qtyEl.textContent = updated.quantity;
        const priceEl = li.querySelector('.cart-item__price');
        priceEl && (priceEl.textContent = formatMoney(updated.final_line_price, cart.currency));
      }
      const subtotalEl = cartDrawer.querySelector('.cart-drawer__subtotal strong');
      subtotalEl && (subtotalEl.textContent = formatMoney(cart.total_price, cart.currency));
      document.querySelectorAll('.cart-badge').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
      if (cart.item_count === 0) {
        // Simplest reliable empty state: reload so Liquid renders it
        window.location.reload();
      }
    } catch (_) {
      /* restore button on failure */
    } finally {
      btn.disabled = false;
    }
  });

  /* ── Product gallery thumbs ─────────────────── */
  const galleryMain = document.getElementById('gallery-main-img');
  document.querySelectorAll('.gallery-thumb').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (galleryMain) galleryMain.src = btn.dataset.src;
      document.querySelectorAll('.gallery-thumb').forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
    });
  });

  /* ── Variant selectors ──────────────────────── */
  document.querySelectorAll('.swatch__input, .size-btn__input').forEach((input) => {
    input.addEventListener('change', () => {
      const group = input.closest('.swatch-group, .size-group');
      if (group) {
        group.querySelectorAll('.swatch, .size-btn').forEach((el) => el.classList.remove('is-active'));
        input.closest('.swatch, .size-btn').classList.add('is-active');
      }
      updateProductForm();
    });
  });

  function updateProductForm() {
    // Collect selected option values and find the matching variant
    const form = document.querySelector('.product-form');
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;

    // Build selected options array from option1, option2, option3
    const options = [];
    [1, 2, 3].forEach((n) => {
      const checked = form.querySelector(`input[name="option${n}"]:checked`);
      if (checked) options.push(checked.value);
    });

    // Find matching variant via Shopify's product JSON endpoint
    const productHandle = window.location.pathname.split('/products/')[1];
    if (!productHandle) return;

    fetch(`/products/${productHandle}.js`)
      .then((r) => r.json())
      .then((data) => {
        const match = data.variants.find((v) =>
          options.every((opt, i) => v[`option${i + 1}`] === opt)
        );
        if (match) {
          idInput.value = match.id;
          const addBtn = form.querySelector('.product-form__add');
          const stickyBtn = document.getElementById('sticky-atc-btn');
          if (addBtn) {
            addBtn.disabled = !match.available;
            addBtn.textContent = match.available
              ? (document.documentElement.dataset.cartAdd || 'Add to Cart')
              : (document.documentElement.dataset.soldOut || 'Sold Out');
          }
          if (stickyBtn) stickyBtn.disabled = !match.available;
        }
      })
      .catch(() => {/* fail silently */});
  }

  /* ── Quick-add buttons on product cards ─────── */
  function wireAtcButton(btn) {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      btn.disabled = true;
      btn.textContent = '...';
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        const res = await fetch('/cart.js');
        const cart = await res.json();
        document.querySelectorAll('.cart-badge').forEach((badge) => {
          badge.textContent = cart.item_count;
          badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
        btn.textContent = '✓';
        setTimeout(() => {
          btn.textContent = document.documentElement.dataset.cartAdd || 'Add to Cart';
          btn.disabled = false;
        }, 1200);
        openCart();
      } catch {
        btn.disabled = false;
        btn.textContent = document.documentElement.dataset.cartAdd || 'Add to Cart';
      }
    });
  }
  document.querySelectorAll('.product-card__atc').forEach(wireAtcButton);

  /* ── Sticky ATC (mobile) ────────────────────── */
  const stickyAtc  = document.getElementById('sticky-atc');
  const formAddBtn = document.querySelector('.product-form__add');
  if (stickyAtc && formAddBtn) {
    const stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        stickyAtc.classList.toggle('is-visible', !e.isIntersecting);
        stickyAtc.setAttribute('aria-hidden', e.isIntersecting ? 'true' : 'false');
      });
    }, { threshold: 0 });
    stickyObserver.observe(formAddBtn);
  }

  /* ── Collection: filter drawer ─────────────── */
  const filterToggle  = document.getElementById('filter-drawer-toggle');
  const filterDrawer  = document.getElementById('filter-drawer');
  const filterOverlay = document.getElementById('filter-overlay');
  const filterClose   = document.getElementById('filter-drawer-close');

  function openFilter() {
    if (!filterDrawer) return;
    filterDrawer.setAttribute('aria-hidden', 'false');
    filterOverlay && filterOverlay.classList.add('is-open');
    filterToggle && filterToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    filterClose && filterClose.focus();
  }
  function closeFilter() {
    if (!filterDrawer) return;
    filterDrawer.setAttribute('aria-hidden', 'true');
    filterOverlay && filterOverlay.classList.remove('is-open');
    filterToggle && filterToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    filterToggle && filterToggle.focus();
  }

  filterToggle  && filterToggle.addEventListener('click', openFilter);
  filterClose   && filterClose.addEventListener('click', closeFilter);
  filterOverlay && filterOverlay.addEventListener('click', closeFilter);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && filterDrawer && filterDrawer.getAttribute('aria-hidden') === 'false') closeFilter();
  });

  // Auto-submit filter form on checkbox change
  const filterForm = document.getElementById('filter-form');
  if (filterForm) {
    filterForm.querySelectorAll('.filter-option__input').forEach((input) => {
      input.addEventListener('change', () => filterForm.submit());
    });
  }

  /* ── Collection: load more (progressive enhancement) ─── */
  const loadMoreBtn = document.getElementById('load-more-btn');
  const productGrid = document.getElementById('product-grid');
  const paginationFallback = document.getElementById('pagination-fallback');

  if (loadMoreBtn && productGrid) {
    // Hide native pagination — JS handles it
    if (paginationFallback) paginationFallback.hidden = true;

    let currentPage = 1;
    const totalPages = parseInt(loadMoreBtn.dataset.pages, 10);

    loadMoreBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      currentPage += 1;
      const url = new URL(window.location.href);
      url.searchParams.set('page', currentPage);

      loadMoreBtn.disabled = true;
      loadMoreBtn.textContent = '…';

      try {
        const res = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const text = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newCards = doc.querySelectorAll('#product-grid .product-card');

        newCards.forEach((card) => {
          productGrid.appendChild(card);
          // Wire ATC buttons on newly appended cards
          const atcBtn = card.querySelector('.product-card__atc');
          if (atcBtn) wireAtcButton(atcBtn);
          // Observe for reveal
          revealObserver.observe(card);
        });

        if (currentPage >= totalPages) {
          loadMoreBtn.closest('#load-more-container').hidden = true;
        } else {
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = loadMoreBtn.getAttribute('aria-label');
        }
      } catch {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = loadMoreBtn.getAttribute('aria-label');
        if (paginationFallback) paginationFallback.hidden = false;
      }
    });
  }

  /* ── Lightbox ───────────────────────────────── */
  const lightbox    = document.getElementById('gallery-lightbox');
  const lightboxImg = lightbox && lightbox.querySelector('.lightbox__img');
  const lightboxClose = lightbox && lightbox.querySelector('.lightbox__close');

  document.querySelectorAll('[data-lightbox]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!lightbox || !lightboxImg) return;
      lightboxImg.src = btn.dataset.lightbox;
      lightboxImg.alt = btn.querySelector('img') ? btn.querySelector('img').alt : '';
      lightbox.showModal();
    });
  });

  lightboxClose && lightboxClose.addEventListener('click', () => lightbox.close());
  lightbox && lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) lightbox.close();
  });

  /* ── Hero carousel ──────────────────────────────────────── */
  class HeroCarousel {
    constructor(el) {
      this.el       = el;
      this.slides   = [...el.querySelectorAll('.hero-slide')];
      this.dots     = [...el.querySelectorAll('.hero-carousel__dot')];
      this.prevBtn  = el.querySelector('.hero-carousel__prev');
      this.nextBtn  = el.querySelector('.hero-carousel__next');
      this.total    = this.slides.length;
      this.current  = 0;
      this.delay    = parseInt(el.dataset.autoplayDelay, 10) || 5000;
      this.timer    = null;
      this.paused   = false;
      this.motionOK = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

      if (this.total < 2) return; // single slide — nothing to do

      this._bindEvents();
      if (this.motionOK) this._startTimer();
    }

    _goTo(index) {
      const prev = this.current;
      this.current = ((index % this.total) + this.total) % this.total;
      if (prev === this.current) return;

      this.slides[prev].classList.remove('is-active');
      this.slides[prev].setAttribute('aria-hidden', 'true');
      if (this.dots[prev]) { this.dots[prev].classList.remove('is-active'); this.dots[prev].setAttribute('aria-selected', 'false'); }

      this.slides[this.current].classList.add('is-active');
      this.slides[this.current].setAttribute('aria-hidden', 'false');
      if (this.dots[this.current]) { this.dots[this.current].classList.add('is-active'); this.dots[this.current].setAttribute('aria-selected', 'true'); }
    }

    _next() { this._goTo(this.current + 1); }
    _prev() { this._goTo(this.current - 1); }

    _startTimer() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (!this.paused) { this._next(); this._startTimer(); }
      }, this.delay);
    }

    _resetTimer() {
      clearTimeout(this.timer);
      if (this.motionOK) this._startTimer();
    }

    _bindEvents() {
      this.nextBtn && this.nextBtn.addEventListener('click', () => { this._next(); this._resetTimer(); });
      this.prevBtn && this.prevBtn.addEventListener('click', () => { this._prev(); this._resetTimer(); });

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this._goTo(i); this._resetTimer(); }));

      // Pause on hover
      this.el.addEventListener('mouseenter', () => { this.paused = true; clearTimeout(this.timer); });
      this.el.addEventListener('mouseleave', () => { this.paused = false; if (this.motionOK) this._startTimer(); });

      // Swipe (touch)
      let startX = 0;
      this.el.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.el.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) { diff > 0 ? this._next() : this._prev(); this._resetTimer(); }
      }, { passive: true });

      // Keyboard arrows (when carousel is focused)
      this.el.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { this._next(); this._resetTimer(); }
        if (e.key === 'ArrowLeft')  { this._prev(); this._resetTimer(); }
      });

      // Theme editor: jump to selected block slide
      document.addEventListener('shopify:block:select', (e) => {
        const idx = this.slides.indexOf(e.target);
        if (idx > -1) { this._goTo(idx); this.paused = true; clearTimeout(this.timer); }
      });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.slides.includes(e.target)) { this.paused = false; if (this.motionOK) this._startTimer(); }
      });
    }
  }

  document.querySelectorAll('.hero--carousel').forEach((el) => new HeroCarousel(el));

  /* ── Magnetic CTA (subtle pointer pull, hero/newsletter primary buttons) ── */
  const motionOK = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (motionOK && window.matchMedia('(hover: hover)').matches) {
    document.querySelectorAll('.btn--magnetic').forEach((btn) => {
      let frame = null;
      btn.addEventListener('mouseenter', () => btn.classList.remove('is-settling'));
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) * 0.18;
        const y = (e.clientY - rect.top - rect.height / 2) * 0.18;
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          btn.style.transform = `translate(${x}px, ${y}px)`;
        });
      });
      btn.addEventListener('mouseleave', () => {
        if (frame) cancelAnimationFrame(frame);
        btn.classList.add('is-settling');
        btn.style.transform = 'translate(0, 0)';
      });
    });
  }

})();
